import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { primaryNavigationItems } from "@/components/ui/navigation-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { scrollToTop, setScrollLocked } from "@/components/common/SmoothScroll";
import PillButton from "@/components/ui/PillButton";
import { useServiceInquiryModal } from "@/hooks/useServiceInquiryModal";
import { getLocalizedRoute, getServiceIdFromPath } from "@/lib/routes";
import { SERVICE_OPTIONS, SITE_CONFIG } from "@/config/site";
import byteetTextDarkLogo from "/byteet_text_dark_logo.svg";

/**
 * The bar holds the logo, the language switch, the quote button and a burger
 * on every page (the switch drops out on the narrowest phones, where it is in
 * the menu). It is drawn in white with
 * `mix-blend-mode: difference`, so it reads as black over paper and white over
 * ink and video, with no background and no scroll listener.
 *
 * The burger opens a full-screen ink panel that wipes down from the top. The
 * pages are set in display type; hovering one brings up a still that belongs
 * to it behind the list. Stills load only once the panel has been opened.
 */
// In menu order: services, cases, articles, about, contact.
const previews = ["/media/web.jpg", "/media/mobile.jpg", "/media/data.jpg", "/media/cloud.jpg", "/media/fiber.jpg"];

const socials = [
  { label: "LinkedIn", href: "https://linkedin.com/company/byteet" },
  { label: "Instagram", href: "https://instagram.com/byteetnl" },
];

const EnhancedNavbar = () => {
  const location = useLocation();
  const { t, language } = useLanguage();
  const { open: openInquiry, prefetch, modal } = useServiceInquiryModal();
  // The menu remembers the page it was opened on, so navigating closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [everOpened, setEverOpened] = useState(false);
  const isOpen = openOn === location.pathname;

  const toggle = () => {
    setEverOpened(true);
    setOpenOn(isOpen ? null : location.pathname);
  };

  const navItems = primaryNavigationItems
    .filter((item) => item.key !== "home")
    .map((item) => ({
      key: item.key,
      name: t(item.translationKey),
      href: getLocalizedRoute(item.href, language),
    }));

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  // Lock the page behind the open menu; close on Escape.
  useEffect(() => {
    setScrollLocked(isOpen);
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpenOn(null);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      setScrollLocked(false);
    };
  }, [isOpen]);

  const quote = () => {
    setOpenOn(null);
    // On a service page, start the sheet with that service ticked.
    const serviceId = getServiceIdFromPath(location.pathname);
    openInquiry(SERVICE_OPTIONS.some((o) => o.id === serviceId) ? [serviceId] : undefined);
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 text-white mix-blend-difference">
        <div className="flex h-16 items-center justify-between px-5 md:h-20 md:px-8">
          <Link
            to={getLocalizedRoute("/", language)}
            aria-label="Byteet - Home"
            className="-ml-1 block"
            onClick={(e) => {
              // Already home: the link would do nothing, so take the visitor
              // back to the top instead.
              if (location.pathname === getLocalizedRoute("/", language)) {
                e.preventDefault();
                setOpenOn(null);
                scrollToTop();
              }
            }}
          >
            <img src={byteetTextDarkLogo} alt="Byteet" width={1270} height={851} className="h-11 w-auto invert md:h-14" />
          </Link>

          <div className="flex items-center gap-3 md:gap-4">
            <LanguageSwitcher className="hidden min-[420px]:grid md:mr-1" />
            <PillButton size="sm" tone="dark" to={getLocalizedRoute(SITE_CONFIG.routes.book, language)} className="border-white/40! max-lg:hidden!">
              {t("nav.bookCall")}
            </PillButton>
            <PillButton size="sm" tone="light" onClick={quote} onMouseEnter={prefetch} onFocus={prefetch} aria-label={t("nav.getConsultation")}>
              <span className="sm:hidden">{t("nav.getConsultationShort")}</span>
              <span className="hidden sm:inline">{t("nav.getConsultation")}</span>
            </PillButton>

            <button
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              aria-controls="site-menu"
              aria-label={isOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              className="group relative flex h-11 w-11 items-center justify-center rounded-byte bg-white transition-transform duration-300 hover:scale-105"
            >
              {/* Two bars that cross into an X. */}
              <span
                aria-hidden="true"
                className={`absolute h-[1.5px] w-5 bg-ink transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isOpen ? "rotate-45" : "-translate-y-[3.5px]"
                }`}
              />
              <span
                aria-hidden="true"
                className={`absolute h-[1.5px] w-5 bg-ink transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isOpen ? "-rotate-45" : "translate-y-[3.5px]"
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* The panel sits outside the blended bar so it paints as plain ink. */}
      <div
        id="site-menu"
        aria-hidden={!isOpen}
        className={`fixed inset-0 z-40 overflow-hidden bg-ink text-white transition-[clip-path,visibility] duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] ${
          isOpen ? "visible [clip-path:inset(0_0_0_0)]" : "invisible [clip-path:inset(0_0_100%_0)]"
        }`}
      >
        {everOpened &&
          navItems.map((item, i) => (
            <img
              key={item.key}
              src={previews[i]}
              alt=""
              aria-hidden="true"
              decoding="async"
              className={`absolute inset-0 h-full w-full object-cover grayscale transition-[opacity,transform] duration-700 ${
                hovered === item.key ? "scale-100 opacity-35" : "scale-105 opacity-0"
              }`}
            />
          ))}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent" />

        <nav aria-label="Menu" className="relative flex h-full flex-col px-5 pb-8 pt-24 md:px-8 md:pb-10 md:pt-28">
          <ul className="flex flex-1 flex-col justify-center" onMouseLeave={() => setHovered(null)}>
            {navItems.map((item, i) => (
              <li key={item.href} className="-mt-[calc(clamp(3rem,min(13vw,12.5vh),10rem)*0.2)] overflow-hidden pt-[calc(clamp(3rem,min(13vw,12.5vh),10rem)*0.2)]">
                <Link
                  to={item.href}
                  tabIndex={isOpen ? 0 : -1}
                  aria-current={isActive(item.href) ? "page" : undefined}
                  onMouseEnter={() => setHovered(item.key)}
                  onFocus={() => setHovered(item.key)}
                  className="group/item flex items-center py-1 md:py-0"
                  style={{
                    transition: "transform 0.9s cubic-bezier(0.22,1,0.36,1)",
                    transitionDelay: isOpen ? `${250 + i * 60}ms` : "0ms",
                    transform: isOpen ? "translate3d(0,0,0)" : "translate3d(0,135%,0)",
                  }}
                >
                  <span
                    className={`display text-[clamp(3rem,min(13vw,12.5vh),10rem)] transition-transform duration-500 group-hover/item:translate-x-4 ${
                      isActive(item.href) ? "" : "text-outline group-hover/item:[-webkit-text-fill-color:currentColor]"
                    }`}
                  >
                    {item.name}
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <div
            className="grid gap-6 border-t border-white/15 pt-6 transition-opacity duration-500 md:grid-cols-12 md:items-end"
            style={{ transitionDelay: isOpen ? "600ms" : "0ms", opacity: isOpen ? 1 : 0 }}
          >
            <div className="label-mono flex flex-col gap-2 text-white/70 md:col-span-5">
              <PillButton size="sm" tone="light" to={getLocalizedRoute(SITE_CONFIG.routes.book, language)} tabIndex={isOpen ? 0 : -1} className="mb-3 self-start">
                {t("nav.bookCall")}
              </PillButton>
              <a href={`mailto:${SITE_CONFIG.email}`} tabIndex={isOpen ? 0 : -1} className="hover:text-white">
                {SITE_CONFIG.email}
              </a>
              <a href={`tel:${SITE_CONFIG.phone}`} tabIndex={isOpen ? 0 : -1} className="hover:text-white">
                {SITE_CONFIG.phoneDisplay}
              </a>
            </div>
            <ul className="label-mono flex gap-6 text-white/60 md:col-span-4">
              {socials.map((s) => (
                <li key={s.label}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" tabIndex={isOpen ? 0 : -1} className="hover:text-white">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            {/* Below 420px the bar has no room for the switch, so it lives here;
                above that the one in the bar is enough. */}
            <LanguageSwitcher className="justify-self-start min-[420px]:hidden md:col-span-3 md:justify-self-end" />
          </div>
        </nav>
      </div>

      {modal}
    </>
  );
};

export default EnhancedNavbar;
